/**
 * The in-app training-video player.
 *
 * A `ResponsiveDialog` (non-form content — see docs/design-system/ui-dialogs.md)
 * around one HTML5 `<video>` streaming the cut straight from the marketing host,
 * where it is already published. Nothing is copied into the app's own storage.
 *
 * Three things here are load-bearing rather than decorative:
 *
 *  - `crossOrigin="anonymous"`. A caption `<track>` is fetched in CORS mode
 *    whatever the media element declares, so without it the captions never
 *    render — and once it is set, the VIDEO fetch is CORS too. Both are served
 *    by the `/videos/tutorials/*` CORS behavior on the marketing distribution
 *    (infrastructure/pulumi/components/marketing_site.py) and admitted by the
 *    SPA's `media-src` (csp_policy.py). Neither of those failures reproduces
 *    locally, where the file is same-origin and no CSP is served.
 *  - `preload="none"` with the cut's own title card as the poster. The mobile
 *    app cold-starts offline and runs over cellular; a ~60 MB cut must not
 *    start downloading because a dialog opened.
 *  - `lang`/`dir` follow the CUT, not the UI. A Spanish reader is offered the
 *    English master (there is no Spanish cut), and a Hebrew cut inside an
 *    English UI still reads right-to-left.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, WifiOff } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { ResponsiveDialog } from '../components/common';
import { useIsOfflineOptional } from '../offline';
import { SUPPORTED_LANGUAGES } from '../../i18n/config';
import {
  captionsUrl,
  isFallbackCut,
  libraryPageUrl,
  posterUrl,
  resolveCut,
  videoUrl,
  type TrainingVideo,
} from './catalog';

/**
 * A cut's language, as the app already describes its own languages. Native name
 * and direction both come from `SUPPORTED_LANGUAGES` rather than a second list —
 * "עברית", not a translated "Hebrew", because the notice names the language the
 * video is IN and that reads best in its own script.
 */
function describeLanguage(code: string): { nativeName: string; dir: 'ltr' | 'rtl' } {
  const known = SUPPORTED_LANGUAGES.find((language) => language.code === code);
  return {
    nativeName: known?.nativeName ?? code.toUpperCase(),
    dir: known?.dir === 'rtl' ? 'rtl' : 'ltr',
  };
}

interface TrainingVideoDialogProps {
  video: TrainingVideo;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fired when the cut plays through to the end — the tile uses it to dismiss itself. */
  onWatched?: () => void;
}

export function TrainingVideoDialog({
  video,
  open,
  onOpenChange,
  onWatched,
}: TrainingVideoDialogProps) {
  const { t, i18n } = useTranslation('common');
  const isOffline = useIsOfflineOptional();
  const [failed, setFailed] = useState(false);

  // A reopen after a failed load deserves a fresh attempt rather than the
  // fallback panel the last one left behind.
  useEffect(() => {
    if (open) setFailed(false);
  }, [open]);

  const cut = resolveCut(video, i18n.language);
  if (!cut) return null;

  const { nativeName: languageName, dir: cutDir } = describeLanguage(cut.language);
  const fallbackNotice = isFallbackCut(cut, i18n.language)
    ? t('help.video.languageFallback', { language: languageName })
    : null;

  const openLibrary = () => window.open(libraryPageUrl(video, cut), '_blank', 'noopener,noreferrer');

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t(video.titleKey)}
      className="max-w-4xl"
    >
      <div className="space-y-3 pb-2">
        <div className="aspect-video w-full overflow-hidden rounded-lg bg-muted">
          {isOffline || failed ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
              <WifiOff className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
              <p className="text-sm text-muted-foreground">
                {isOffline ? t('help.video.offline') : t('help.video.loadFailed')}
              </p>
              <Button variant="outline" size="sm" onClick={openLibrary}>
                <ExternalLink className="h-4 w-4" aria-hidden="true" />
                <span className="ms-2">{t('help.video.openInLibrary')}</span>
              </Button>
            </div>
          ) : (
            <video
              key={`${video.slug}-${cut.language}`}
              className="h-full w-full"
              controls
              playsInline
              preload="none"
              crossOrigin="anonymous"
              poster={posterUrl(video)}
              lang={cut.language}
              onEnded={onWatched}
              onError={() => setFailed(true)}
              // `src` on the element, NOT a <source> child. With a child, the
              // resource-selection algorithm fires `error` at the <source> and
              // then merely sets networkState = NETWORK_NO_SOURCE — the media
              // element never fires one — so `onError` is unreachable and a cut
              // that fails to load renders as a silent black box. Caught in a
              // browser, against the not-yet-CORSed production asset; jsdom has
              // no resource-selection algorithm, so no unit test can tell the
              // two spellings apart and none is asserted to.
              src={videoUrl(video, cut)}
              data-testid="training-video-player"
            >
              <track
                kind="captions"
                src={captionsUrl(video, cut)}
                srcLang={cut.language}
                label={languageName}
                default
              />
            </video>
          )}
        </div>

        <p className="text-sm text-muted-foreground" dir={cutDir} lang={cut.language}>
          {t(video.descriptionKey)}
        </p>

        {fallbackNotice && (
          <p className="text-xs text-muted-foreground">{fallbackNotice}</p>
        )}
      </div>
    </ResponsiveDialog>
  );
}
